import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';
import loadable from '@loadable/component';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import CatalogComponent from '@/components/CatalogComponent/CatalogComponent';
import { CompleteAccountBanner } from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import { RegisterAuctionBanner } from '@/components/RegisterAuctionBanner/RegisterAuctionBanner';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogIsTimedPlus,
    getCatalogTitle,
    getShouldShowLive,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { getIdFromPath } from '@/utils/urls';

import { CatalogFooter } from './CatalogFooter';
import CatalogPageBreadcrumbs from './CatalogPageBreadcrumbs';
import CatalogPageHeaderMeta from './CatalogPageHeaderMeta';
import CatalogPageItems from './CatalogPageItems';

/** Establish connection to real-time auction events, only on the client-side */
const LiveAuctionEventsSubscriber = loadable(
    () => import('@/components/LiveEventSubscribers/LiveAuctionEventsSubscriber'),
    {
        ssr: false,
    }
);

type RouteParams = {
    catalogId: string;
};

const CatalogPage = () => {
    const dispatch = useAppDispatch();

    const whiteLabel = useAppSelector(getIsWhiteLabel);
    const { catalogId: catalogIdString } = useParams<RouteParams>();
    const catalogId = getIdFromPath(catalogIdString);
    const showLive = useAppSelector((state) => getShouldShowLive(state, catalogId));
    const timedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const [showBanner, setShowBanner] = useState(false);
    const isMobile = useWindowIsMobile();

    const scrolling = useCallback(
        (e) => {
            e.preventDefault();
            if (window.pageYOffset >= 570 && !isMobile) {
                setShowBanner(true);
            } else {
                setShowBanner(false);
            }
        },
        [isMobile]
    );

    useEffect(() => {
        window?.addEventListener('scroll', scrolling);

        return () => {
            window?.removeEventListener('scroll', scrolling);
        };
    }, [dispatch, catalogId, scrolling]);

    if (!title) {
        return null;
    }

    return (
        <div className="flex flex-col">
            <CatalogPageHeaderMeta catalogId={catalogId} />
            {(showLive || timedPlus) && (
                <LiveAuctionEventsSubscriber
                    catalogId={catalogId}
                    limitMessagesFilter="catalogPageConsole"
                />
            )}
            <CompleteAccountBanner />
            <RegisterAuctionBanner
                catalogId={catalogId}
                show={showBanner}
            />
            {!whiteLabel && <CatalogPageBreadcrumbs catalogId={catalogId} />}
            <CatalogComponent catalogId={catalogId} />
            <CatalogPageItems catalogId={catalogId} />
            <CatalogFooter catalogId={catalogId} />
        </div>
    );
};

export default CatalogPage;
